import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import userBookingService, { UserBooking } from '../../services/userBookingService';

interface UserBookingsState {
  bookings: UserBooking[];
  currentBooking: UserBooking | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  } | null;
  isLoading: boolean;
  error: string | null;
  success: boolean;
}

const initialState: UserBookingsState = {
  bookings: [],
  currentBooking: null,
  pagination: null,
  isLoading: false,
  error: null,
  success: false,
};

// Async thunks
export const fetchUserBookings = createAsyncThunk(
  'userBookings/fetchUserBookings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userBookingService.getUserBookings();
      return response.data; // response is {statusCode, message, data}, so response.data is the bookings array
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch bookings');
    }
  }
);

export const fetchBookingById = createAsyncThunk(
  'userBookings/fetchBookingById',
  async (bookingId: string, { rejectWithValue }) => {
    try {
      const response = await userBookingService.getBookingById(bookingId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch booking');
    }
  }
);

export const cancelUserBooking = createAsyncThunk(
  'userBookings/cancelUserBooking',
  async (bookingId: string, { rejectWithValue }) => {
    try {
      const response = await userBookingService.cancelBooking(bookingId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel booking');
    }
  }
);

export const updateUserBooking = createAsyncThunk(
  'userBookings/updateUserBooking',
  async ({ bookingId, updateData }: { bookingId: string; updateData: Partial<UserBooking> }, { rejectWithValue }) => {
    try {
      const response = await userBookingService.updateBooking(bookingId, updateData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update booking');
    }
  }
);

const userBookingsSlice = createSlice({
  name: 'userBookings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    setCurrentBooking: (state, action: PayloadAction<UserBooking>) => {
      state.currentBooking = action.payload;
    },
    clearCurrentBooking: (state) => {
      state.currentBooking = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch user bookings
      .addCase(fetchUserBookings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserBookings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bookings = action.payload.data; // Extract the data array
        state.pagination = action.payload.pagination; // Store pagination info
        state.success = true;
      })
      .addCase(fetchUserBookings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch booking by ID
      .addCase(fetchBookingById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBookingById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentBooking = action.payload;
        state.success = true;
      })
      .addCase(fetchBookingById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Cancel booking
      .addCase(cancelUserBooking.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelUserBooking.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        // Update the booking in the list
        const index = state.bookings.findIndex(booking => booking._id === action.payload._id);
        if (index !== -1) {
          state.bookings[index] = action.payload;
        }
        // Update current booking if it's the same one
        if (state.currentBooking?._id === action.payload._id) {
          state.currentBooking = action.payload;
        }
      })
      .addCase(cancelUserBooking.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update booking
      .addCase(updateUserBooking.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserBooking.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        // Update the booking in the list
        const index = state.bookings.findIndex(booking => booking._id === action.payload._id);
        if (index !== -1) {
          state.bookings[index] = action.payload;
        }
        // Update current booking if it's the same one
        if (state.currentBooking?._id === action.payload._id) {
          state.currentBooking = action.payload;
        }
      })
      .addCase(updateUserBooking.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSuccess, setCurrentBooking, clearCurrentBooking } = userBookingsSlice.actions;

export default userBookingsSlice.reducer;